import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import AppShell, { Protected } from "@/components/AppShell";
import { api } from "@/lib/api";
import { normalizeText } from "@/lib/text";
import { useAuth } from "@/lib/auth";
import { suggestedCategoriesFor } from "@/lib/specialtyTemplates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, PencilSimple, Trash, Copy, QrCode, MagnifyingGlass, WarningCircle, FileText, BookBookmark, CheckCircle, Pill, Lightning, HandHeart, Bone, Brain, Users, Moon, Drop, ArrowRight, Heartbeat, Wind, Syringe, Palette, GenderFemale, Baby, Eye, Ear, ForkKnife, PushPin, PushPinSlash, PaperPlaneTilt, Paperclip, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import QRDialog from "@/components/QRDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import TemplatePreviewDialog from "@/components/TemplatePreviewDialog";
import AITemplateDialog from "@/components/AITemplateDialog";
import { Sparkle } from "@phosphor-icons/react";
import { useAccessGate } from "@/hooks/useAccessGate";

export default function Templates() {
  const [aiOpen, setAiOpen] = useState(false);
  const { user } = useAuth();
  const { isTrialExpired, gateProps } = useAccessGate();
  const [mine, setMine] = useState([]);
  const [library, setLibrary] = useState([]);
  const [q, setQ] = useState("");
  const [qrTpl, setQrTpl] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [importing, setImporting] = useState(null);
  const [previewTpl, setPreviewTpl] = useState(null);
  // Warn the practitioner before editing a template that patients are answering
  const [pendingEdit, setPendingEdit] = useState(null); // {template_id, count} | null
  const [checkingEdit, setCheckingEdit] = useState(null); // template_id being probed
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "library" ? "library" : "mine";
  const setTab = (v) => setParams((p) => { const np = new URLSearchParams(p); if (v === "library") np.set("tab", "library"); else np.delete("tab"); return np; }, { replace: true });

  const load = async () => {
    const { data } = await api.get("/templates");
    setMine(data.filter((t) => !t.is_validated));
    setLibrary(data.filter((t) => t.is_validated));
  };
  useEffect(() => { load(); }, []);

  const goToEdit = (t) => nav(`/templates/${t.template_id}/edit`);
  const askEdit = async (t, e) => {
    if (e) e.preventDefault();
    setCheckingEdit(t.template_id);
    try {
      const { data } = await api.get(`/templates/${t.template_id}/active-drafts`);
      if (data.count > 0) {
        setPendingEdit({ template_id: t.template_id, title: t.title, count: data.count });
      } else {
        goToEdit(t);
      }
    } catch {
      // fail-open: still let them edit if the check fails
      goToEdit(t);
    } finally { setCheckingEdit(null); }
  };

  const dup = async (id) => {
    try { const { data } = await api.post(`/templates/${id}/duplicate`); toast.success("Dupliqué"); nav(`/templates/${data.template_id}/edit`); }
    catch { toast.error("Erreur"); }
  };
  const togglePin = async (t) => {
    try {
      const { data } = await api.post(`/templates/${t.template_id}/pin`);
      setMine((prev) => prev.map((x) => x.template_id === t.template_id ? { ...x, is_pinned: data.is_pinned } : x));
      toast.success(data.is_pinned ? `« ${t.title} » épinglé` : `« ${t.title} » désépinglé`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'épingler");
    }
  };
  const importFromLibrary = async (t) => {
    setImporting(t.template_id);
    try {
      const { data } = await api.post(`/templates/${t.template_id}/duplicate`);
      toast.success(`« ${t.title} » ajouté à vos questionnaires`);
      nav(`/templates/${data.template_id}/edit`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import impossible");
    } finally { setImporting(null); }
  };
  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/templates/${pendingDelete.template_id}`);
      toast.success("Questionnaire supprimé");
      setPendingDelete(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setDeleting(false); }
  };

  const matchQ = (t) => {
    if (!q) return true;
    const s = normalizeText(q);
    return normalizeText(`${t.title} ${t.category || ""} ${t.description || ""}`).includes(s);
  };
  const filtered = mine.filter(matchQ).sort((a, b) => {
    if (!!a.is_pinned !== !!b.is_pinned) return a.is_pinned ? -1 : 1;
    return (b.updated_at || "").localeCompare(a.updated_at || "");
  });
  const pinnedCount = mine.filter((t) => t.is_pinned).length;
  const filteredLib = library.filter(matchQ);
  const libByCategory = filteredLib.reduce((acc, t) => {
    const c = t.category || "Autre";
    (acc[c] = acc[c] || []).push(t);
    return acc;
  }, {});
  const mineSlugs = new Set(mine.map((t) => t.slug).filter(Boolean));

  // One icon per specialty. Fallback = FileText.
  const CATEGORY_ICONS = {
    "Addictologie": Pill,
    "Cardiologie": Heartbeat,
    "Dermatologie": Palette,
    "Diabétologie": Syringe,
    "Douleur": Lightning,
    "Gastro-entérologie": ForkKnife,
    "Gériatrie": HandHeart,
    "Gynécologie": GenderFemale,
    "Neurologie": Brain,
    "Ophtalmologie": Eye,
    "ORL": Ear,
    "Pédiatrie": Baby,
    "Pneumologie": Wind,
    "Rhumatologie": Bone,
    "Santé mentale": Brain,
    "Santé publique": Users,
    "Sommeil": Moon,
    "Urologie": Drop,
  };
  const catIcon = (c) => CATEGORY_ICONS[c] || FileText;

  const [libCat, setLibCat] = useState("all");
  const libCategoryCounts = library.reduce((acc, t) => {
    const c = t.category || "Autre";
    acc[c] = (acc[c] || 0) + 1;
    return acc;
  }, {});
  const sortedCategories = Object.keys(libCategoryCounts).sort((a, b) => a.localeCompare(b, "fr"));
  const filteredLibByCat = filteredLib.filter((t) => libCat === "all" || (t.category || "Autre") === libCat);
  const groupedLib = filteredLibByCat.reduce((acc, t) => {
    const c = t.category || "Autre";
    (acc[c] = acc[c] || []).push(t);
    return acc;
  }, {});

  // --- Suggest a validated questionnaire ---
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggest, setSuggest] = useState({ title: "", reference: "", description: "" });
  const [suggestPdf, setSuggestPdf] = useState(null);
  const [suggestSending, setSuggestSending] = useState(false);
  const resetSuggest = () => { setSuggest({ title: "", reference: "", description: "" }); setSuggestPdf(null); };
  const submitSuggest = async (e) => {
    e.preventDefault();
    const title = suggest.title.trim();
    const reference = suggest.reference.trim();
    const description = suggest.description.trim();
    if (!title || !reference || !description) {
      toast.error("Titre, référence et description sont requis"); return;
    }
    if (suggestPdf) {
      if (suggestPdf.size > 5 * 1024 * 1024) { toast.error("Le PDF doit peser moins de 5 Mo"); return; }
      if (!suggestPdf.name.toLowerCase().endsWith(".pdf")) { toast.error("Seul un PDF est accepté"); return; }
    }
    setSuggestSending(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("reference", reference);
      fd.append("description", description);
      if (suggestPdf) fd.append("pdf", suggestPdf);
      await api.post("/questionnaire-suggestions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Suggestion envoyée — merci !");
      setSuggestOpen(false);
      resetSuggest();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Envoi impossible");
    } finally {
      setSuggestSending(false);
    }
  };

  return (
    <Protected role="admin">
      <AppShell
        title="Questionnaires"
        actions={
          tab === "library" ? (
            <Button
              onClick={() => setSuggestOpen(true)}
              variant="outline"
              className="rounded-[10px] border-[#3A8FA5] text-[#3A8FA5] hover:bg-[#E6F1F5] hover:text-[#1E3A5F] font-semibold"
              data-testid="suggest-template-btn"
            >
              <PaperPlaneTilt size={16} className="mr-2" weight="duotone" /> Suggérer un questionnaire
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                className="rounded-[10px] border-[#3A8FA5] text-[#3A8FA5] hover:bg-[#E6F1F5] hover:text-[#1E3A5F] font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="ai-template-btn"
                {...gateProps({ onClick: () => setAiOpen(true), title: "Créer avec l'IA" })}
              >
                <Sparkle size={16} className="mr-2" weight="duotone" /> Créer avec l'IA
              </Button>
              {isTrialExpired ? (
                <Button
                  className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="new-template-btn"
                  {...gateProps({ title: "Nouveau questionnaire" })}
                >
                  <Plus size={16} className="mr-2" /> Nouveau questionnaire
                </Button>
              ) : (
                <Link to="/templates/new" data-testid="new-template-btn">
                  <Button className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold">
                    <Plus size={16} className="mr-2" /> Nouveau questionnaire
                  </Button>
                </Link>
              )}
            </div>
          )
        }
      >
        <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="inline-flex items-center gap-1 bg-white border border-[#E1E9F0] rounded-[14px] p-1" role="tablist">
            <button
              type="button"
              onClick={() => setTab("mine")}
              className={`px-4 py-2 rounded-[10px] text-[14px] font-semibold inline-flex items-center gap-2 transition-colors ${tab === "mine" ? "bg-[#5F758F] text-white" : "text-[#5F758F] hover:text-[#1E3A5F]"}`}
              data-testid="tpl-tab-mine"
            >
              <FileText size={16} weight="duotone" /> Mes questionnaires
              <span className={`text-[12px] px-2 py-0.5 rounded-full font-bold ${tab === "mine" ? "bg-white/15 text-white" : "bg-[#E6F1F5] text-[#3A8FA5]"}`}>{mine.length}</span>
            </button>
            <button
              type="button"
              onClick={() => setTab("library")}
              className={`px-4 py-2 rounded-[10px] text-[14px] font-semibold inline-flex items-center gap-2 transition-colors ${tab === "library" ? "bg-[#5F758F] text-white" : "text-[#5F758F] hover:text-[#1E3A5F]"}`}
              data-testid="tpl-tab-library"
            >
              <BookBookmark size={16} weight="duotone" /> Bibliothèque validée
              <span className={`text-[12px] px-2 py-0.5 rounded-full font-bold ${tab === "library" ? "bg-white/15 text-white" : "bg-[#E6F1F5] text-[#3A8FA5]"}`}>{library.length}</span>
            </button>
          </div>
          <div className="relative w-full md:w-80">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#5F758F]" />
            <Input
              placeholder={tab === "library" ? "Rechercher dans la bibliothèque…" : "Rechercher…"}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9 bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5]"
              data-testid="tpl-search"
            />
          </div>
        </div>

        {tab === "mine" && (filtered.length === 0 ? (
          <div className="space-y-6">
            <div className="bg-white border border-[#E1E9F0] rounded-2xl p-10 text-center">
              <p className="text-2xl font-bold text-[#1E3A5F] mb-3">Aucun questionnaire</p>
              <p className="text-[#5F758F] mb-6">Créez le vôtre à partir de zéro ou d'un questionnaire validé (EVA, PHQ-9, GAD-7, Oswestry, IPSS…).</p>
              <Link to="/templates/new">
                <Button className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold">
                  Créer un questionnaire
                </Button>
              </Link>
            </div>
            {(() => {
              const suggestedCats = suggestedCategoriesFor(user?.specialty);
              if (!suggestedCats.length || !library.length) return null;
              const suggested = library
                .filter((t) => suggestedCats.includes(t.category || "Autre"))
                .filter((t) => !t.slug || !mineSlugs.has(t.slug))
                .slice(0, 6);
              if (!suggested.length) return null;
              return (
                <div className="bg-[#F4F7FB] border border-[#E1E9F0] rounded-2xl p-6" data-testid="specialty-suggestions">
                  <div className="flex items-center gap-2 mb-4">
                    <BookBookmark size={18} weight="duotone" className="text-[#3A8FA5]" />
                    <h3 className="font-serif text-lg text-[#1E3A5F]">Suggéré pour votre spécialité</h3>
                    {user?.specialty && (
                      <span className="text-[11px] tracking-[0.14em] uppercase text-[#5F758F] font-semibold">· {user.specialty}</span>
                    )}
                  </div>
                  <div className="grid gap-2">
                    {suggested.map((t) => {
                      const Icon = catIcon(t.category);
                      return (
                        <div key={t.template_id} className="bg-white border border-[#E1E9F0] rounded-[12px] px-4 py-3 flex items-center gap-3">
                          <span className="w-9 h-9 rounded-[10px] bg-[#E6F1F5] border border-[#C7DEE6] flex items-center justify-center shrink-0">
                            <Icon size={16} weight="duotone" className="text-[#3A8FA5]" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-[14px] font-semibold text-[#1E3A5F] leading-tight">{t.title}</p>
                            <p className="text-[12px] text-[#5F758F] mt-0.5">{t.category} · {t.questions.length} Q</p>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-[10px] border-[#E1E9F0] text-[#3A8FA5] hover:bg-[#3A8FA5] hover:text-white hover:border-[#3A8FA5] font-semibold h-8 px-3 text-[13px]"
                            onClick={() => importFromLibrary(t)}
                            disabled={importing === t.template_id}
                            data-testid={`suggest-import-${t.slug || t.template_id}`}
                          >
                            {importing === t.template_id ? "Ajout…" : "Utiliser"}
                            <ArrowRight size={13} className="ml-1.5" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {pinnedCount > 0 && (
              <p className="text-[11px] tracking-[0.14em] uppercase text-[#5F758F] font-semibold flex items-center gap-2 mb-1">
                <PushPin size={12} weight="fill" className="text-[#3A8FA5]" />
                Épinglés · {pinnedCount} / 5
              </p>
            )}
            {filtered.map((t, idx) => {
              const isFirstUnpinned = pinnedCount > 0 && !t.is_pinned && (idx === 0 || filtered[idx - 1]?.is_pinned);
              return (
              <div key={t.template_id}>
                {isFirstUnpinned && (
                  <p className="text-[11px] tracking-[0.14em] uppercase text-[#5F758F] font-semibold mt-4 mb-1">Autres questionnaires</p>
                )}
                <div
                  className={`bg-white border rounded-[14px] px-6 py-5 grid grid-cols-[auto_1fr_auto] gap-4 items-center transition-all hover:border-[#3A8FA5] hover:shadow-[0_2px_8px_rgba(58,143,165,0.08)] ${t.is_pinned ? "border-[#C7DEE6] ring-1 ring-[#E6F1F5]" : "border-[#E1E9F0]"}`}
                  data-testid={`tpl-${t.template_id}`}
                >
                  <button
                    type="button"
                    {...gateProps({
                      onClick: () => togglePin(t),
                      title: t.is_pinned ? "Désépingler" : "Épingler en haut de la liste",
                    })}
                    className={`w-9 h-9 rounded-[10px] flex items-center justify-center transition-colors shrink-0 disabled:opacity-40 disabled:cursor-not-allowed ${t.is_pinned ? "bg-[#E6F1F5] border border-[#C7DEE6] text-[#3A8FA5]" : "text-[#B7C4D6] hover:text-[#3A8FA5] hover:bg-[#F4F7FB]"}`}
                    data-testid={`pin-${t.template_id}`}
                    aria-label={t.is_pinned ? "Désépingler" : "Épingler"}
                  >
                    {t.is_pinned ? <PushPin size={16} weight="fill" /> : <PushPin size={16} />}
                  </button>
                  {(() => {
                    const isOwn = !t.owner_id || t.owner_id === user?.user_id;
                    return (
                      <Link to={`/templates/${t.template_id}/edit`} onClick={(e) => isOwn && askEdit(t, e)} className="min-w-0 block" data-testid={`edit-title-${t.template_id}`}>
                        <p className="text-[16px] font-semibold text-[#1E3A5F] flex items-center gap-2 flex-wrap">
                          <span>{t.title}</span>
                          {t.visibility === "service" && isOwn && (
                            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.12em] uppercase font-bold px-1.5 py-0.5 rounded-full bg-[#E6F1F5] text-[#3A8FA5] border border-[#C7DEE6]" data-testid={`shared-${t.template_id}`} title="Partagé au service">
                              Partagé
                            </span>
                          )}
                          {!isOwn && (
                            <span className="inline-flex items-center gap-1 text-[10px] tracking-[0.12em] uppercase font-bold px-1.5 py-0.5 rounded-full bg-[#EEF3F8] text-[#5F758F] border border-[#E1E9F0]" data-testid={`from-service-${t.template_id}`} title={t.owner_name ? `Partagé par ${t.owner_name}` : "Reçu du service"}>
                              Du service
                            </span>
                          )}
                        </p>
                        <p className="text-[14px] text-[#5F758F] mt-0.5">
                          {t.category || "—"}
                          <span className="mx-1.5">·</span>
                          {t.questions.length} question{t.questions.length > 1 ? "s" : ""}
                          {!isOwn && t.owner_name && (
                            <>
                              <span className="mx-1.5">·</span>
                              <span className="italic">partagé par {t.owner_name}{t.owner_cabinet_name ? ` (${t.owner_cabinet_name})` : ""}</span>
                            </>
                          )}
                        </p>
                      </Link>
                    );
                  })()}
                  <div className="flex gap-1 items-center shrink-0">
                  {(() => {
                    const isOwn = !t.owner_id || t.owner_id === user?.user_id;
                    return (
                      <>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-[10px] text-[#5F758F] hover:text-[#3A8FA5] hover:bg-[#E6F1F5]"
                    onClick={() => setQrTpl(t)}
                    data-testid={`qr-${t.template_id}`}
                    title="QR cabinet"
                  >
                    <QrCode size={16} />
                  </Button>
                  {isOwn && (
                  <Link to={`/templates/${t.template_id}/edit`} onClick={(e) => askEdit(t, e)}>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={checkingEdit === t.template_id}
                      className="rounded-[10px] text-[#5F758F] hover:text-[#3A8FA5] hover:bg-[#E6F1F5]"
                      data-testid={`edit-${t.template_id}`}
                      title="Modifier"
                    >
                      <PencilSimple size={16} />
                    </Button>
                  </Link>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-[10px] text-[#5F758F] hover:text-[#3A8FA5] hover:bg-[#E6F1F5] disabled:opacity-40 disabled:cursor-not-allowed"
                    title="Dupliquer"
                    {...gateProps({ onClick: () => dup(t.template_id), title: "Dupliquer" })}
                  >
                    <Copy size={16} />
                  </Button>
                  {isOwn && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="rounded-[10px] text-[#5F758F] hover:text-[#D45A3F] hover:bg-[#FFE0DC] disabled:opacity-40 disabled:cursor-not-allowed"
                    data-testid={`delete-${t.template_id}`}
                    title="Supprimer"
                    {...gateProps({ onClick: () => setPendingDelete(t), title: "Supprimer" })}
                  >
                    <Trash size={16} />
                  </Button>
                  )}
                      </>
                    );
                  })()}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        ))}
        {tab === "library" && (Object.keys(libByCategory).length === 0 ? (
          <div className="bg-white border border-[#E1E9F0] rounded-2xl p-16 text-center">
            <p className="text-2xl font-bold text-[#1E3A5F] mb-3">Aucun résultat</p>
            <p className="text-[#5F758F]">Aucun questionnaire validé ne correspond à votre recherche.</p>
          </div>
        ) : (
          <div>
            {/* Category filter pills — horizontal scroll on small screens */}
            <div className="mb-6 -mx-1 flex flex-wrap items-center gap-2" data-testid="lib-category-filters">
              <button
                type="button"
                onClick={() => setLibCat("all")}
                className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[13px] font-semibold border transition-colors ${libCat === "all" ? "bg-[#1E3A5F] text-white border-[#1E3A5F]" : "bg-white text-[#5F758F] border-[#E1E9F0] hover:border-[#3A8FA5] hover:text-[#1E3A5F]"}`}
                data-testid="lib-cat-all"
              >
                Toutes
                <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${libCat === "all" ? "bg-white/15 text-white" : "bg-[#F4F7FB] text-[#5F758F]"}`}>{library.length}</span>
              </button>
              {sortedCategories.map((c) => {
                const Icon = catIcon(c);
                const active = libCat === c;
                return (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setLibCat(c)}
                    className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-[13px] font-semibold border transition-colors ${active ? "bg-[#1E3A5F] text-white border-[#1E3A5F]" : "bg-white text-[#5F758F] border-[#E1E9F0] hover:border-[#3A8FA5] hover:text-[#1E3A5F]"}`}
                    data-testid={`lib-cat-${c}`}
                  >
                    <Icon size={14} weight="duotone" className={active ? "text-white" : "text-[#3A8FA5]"} />
                    {c}
                    <span className={`text-[11px] px-1.5 py-0.5 rounded-full font-bold ${active ? "bg-white/15 text-white" : "bg-[#F4F7FB] text-[#5F758F]"}`}>{libCategoryCounts[c]}</span>
                  </button>
                );
              })}
            </div>

            {Object.keys(groupedLib).length === 0 ? (
              <div className="bg-white border border-[#E1E9F0] rounded-2xl p-12 text-center">
                <p className="text-[#5F758F]">Aucun questionnaire dans cette catégorie ne correspond à votre recherche.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-8">
                {Object.entries(groupedLib).sort(([a], [b]) => a.localeCompare(b, "fr")).map(([cat, tpls]) => {
                  const Icon = catIcon(cat);
                  return (
                    <div key={cat}>
                      <div className="flex items-center gap-2.5 mb-2.5">
                        <Icon size={16} weight="duotone" className="text-[#3A8FA5]" />
                        <h4 className="text-[11px] tracking-[0.14em] uppercase text-[#5F758F] font-semibold">{cat}</h4>
                        <span className="text-[12px] text-[#5F758F]">{tpls.length}</span>
                        <span className="h-px flex-1 bg-[#E1E9F0]" />
                      </div>
                      <div className="bg-white border border-[#E1E9F0] rounded-[14px] divide-y divide-[#E1E9F0] overflow-hidden">
                        {tpls.map((t) => {
                          const already = t.slug && mineSlugs.has(t.slug);
                          return (
                            <div
                              key={t.template_id}
                              role="button"
                              tabIndex={0}
                              onClick={() => setPreviewTpl(t)}
                              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setPreviewTpl(t); } }}
                              className="group grid grid-cols-[auto_1fr_auto] items-center gap-4 px-5 py-3.5 transition-colors hover:bg-[#F4F7FB] cursor-pointer focus:outline-none focus-visible:bg-[#F4F7FB]"
                              data-testid={`lib-tpl-${t.slug || t.template_id}`}
                            >
                              <span className="w-9 h-9 rounded-[10px] bg-[#E6F1F5] border border-[#C7DEE6] flex items-center justify-center shrink-0">
                                <Icon size={16} weight="duotone" className="text-[#3A8FA5]" />
                              </span>
                              <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <p className="text-[14.5px] font-semibold text-[#1E3A5F] leading-tight">{t.title}</p>
                                  {already && (
                                    <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-[#3A8FA5] font-semibold whitespace-nowrap bg-[#E6F1F5] border border-[#C7DEE6] rounded-full px-2 py-0.5">
                                      <CheckCircle size={11} weight="fill" /> Ajouté
                                    </span>
                                  )}
                                </div>
                                <p className="text-[12.5px] text-[#5F758F] mt-1 line-clamp-1" title={t.description || ""}>{t.description || "—"}</p>
                              </div>
                              <div className="flex items-center gap-3 shrink-0">
                                <span className="text-[12px] text-[#5F758F] tabular-nums hidden sm:inline">{t.questions.length} Q</span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="rounded-[10px] border-[#E1E9F0] text-[#3A8FA5] hover:bg-[#3A8FA5] hover:text-white hover:border-[#3A8FA5] font-semibold h-8 px-3 text-[13px]"
                                  onClick={(e) => { e.stopPropagation(); importFromLibrary(t); }}
                                  disabled={importing === t.template_id}
                                  data-testid={`lib-import-${t.slug || t.template_id}`}
                                >
                                  {importing === t.template_id ? "Ajout…" : "Utiliser"}
                                  <ArrowRight size={13} className="ml-1.5" />
                                </Button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
        <QRDialog template={qrTpl} onClose={() => setQrTpl(null)} />

        <TemplatePreviewDialog
          open={!!previewTpl}
          template={previewTpl}
          onClose={() => setPreviewTpl(null)}
          onUse={() => { if (previewTpl) { const t = previewTpl; setPreviewTpl(null); importFromLibrary(t); } }}
          useLabel={previewTpl?.slug && mineSlugs.has(previewTpl.slug) ? "Ajouter une copie" : "Utiliser ce modèle"}
          useBusy={importing === previewTpl?.template_id}
          headerLabel="Aperçu du modèle"
        />

        <AlertDialog open={!!pendingEdit} onOpenChange={(v) => { if (!v) setPendingEdit(null); }}>
          <AlertDialogContent className="bg-white" data-testid="edit-warning-dialog">
            <AlertDialogHeader>
              <div className="mx-auto sm:mx-0 mb-2 w-12 h-12 rounded-[14px] bg-[#FFF3D6] border border-[#F0DDA8] flex items-center justify-center">
                <WarningCircle size={26} weight="duotone" className="text-[#D45A3F]" />
              </div>
              <AlertDialogTitle className="text-2xl font-bold tracking-tight text-[#1E3A5F]">
                {pendingEdit?.count === 1
                  ? "1 patient est en train de répondre au questionnaire"
                  : `${pendingEdit?.count || 0} patients sont en train de répondre au questionnaire`}
              </AlertDialogTitle>
              <AlertDialogDescription className="text-[#5F758F] leading-relaxed space-y-3">
                <span className="block">
                  Si vous le modifiez maintenant, les patients concernés verront la <strong>nouvelle version</strong> lorsqu'ils reprendront leur questionnaire et leurs réponses aux questions supprimées seront <strong>effacées</strong>. Les questionnaires déjà complétés ne sont pas touchés.
                </span>
                <span className="block text-xs italic">
                  Astuce : si le changement est important, nous vous conseillons de laisser ces patients terminer d'abord ou de créer un nouveau questionnaire en dupliquant celui-ci.
                </span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-[10px]" data-testid="edit-warning-cancel">Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => {
                  e.preventDefault();
                  const t = pendingEdit; setPendingEdit(null);
                  if (t) nav(`/templates/${t.template_id}/edit`);
                }}
                className="rounded-[10px] bg-[#D45A3F] hover:bg-[#8B2A1E] text-white font-semibold"
                data-testid="edit-warning-confirm"
              >
                Modifier quand même
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <AlertDialog open={!!pendingDelete} onOpenChange={(v) => { if (!v && !deleting) setPendingDelete(null); }}>
          <AlertDialogContent className="bg-white">
            <AlertDialogHeader>
              <div className="mx-auto sm:mx-0 mb-2 w-12 h-12 rounded-[14px] bg-[#FFE0DC] border border-[#EEC1BB] flex items-center justify-center">
                <WarningCircle size={26} weight="duotone" className="text-[#8B2A1E]" />
              </div>
              <AlertDialogTitle className="text-2xl font-bold tracking-tight text-[#1E3A5F]">Supprimer ce questionnaire ?</AlertDialogTitle>
              <AlertDialogDescription className="text-[#5F758F] leading-relaxed">
                Vous êtes sur le point de supprimer <strong className="text-[#1E3A5F]">« {pendingDelete?.title} »</strong>. Cette action est <strong className="text-[#8B2A1E]">irréversible</strong> — le modèle disparaîtra définitivement de votre bibliothèque.
                <span className="block mt-3 rounded-[10px] bg-[#FFE0DC] border border-[#EEC1BB] px-3 py-2 text-[#8B2A1E]">
                  Les questionnaires <strong>déjà envoyés et toujours en attente de réponse</strong> ne seront plus accessibles aux patients — leurs liens deviendront invalides.
                </span>
                <span className="block mt-3 text-xs text-[#5F758F]">Les réponses déjà collectées, elles, sont conservées.</span>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="rounded-[10px]" disabled={deleting} data-testid="delete-cancel">Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={(e) => { e.preventDefault(); confirmDelete(); }}
                disabled={deleting}
                className="rounded-[10px] bg-[#8B2A1E] hover:bg-[#6D2117] text-white font-semibold"
                data-testid="delete-confirm"
              >
                {deleting ? "Suppression…" : "Supprimer définitivement"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Suggest a validated questionnaire */}
        <Dialog open={suggestOpen} onOpenChange={(v) => { if (!v && !suggestSending) { setSuggestOpen(false); resetSuggest(); } }}>
          <DialogContent className="bg-white sm:max-w-[560px]" data-testid="suggest-dialog">
            <DialogHeader>
              <div className="mb-2 w-12 h-12 rounded-[14px] bg-[#E6F1F5] border border-[#C7DEE6] flex items-center justify-center">
                <PaperPlaneTilt size={22} weight="duotone" className="text-[#3A8FA5]" />
              </div>
              <DialogTitle className="text-2xl font-bold tracking-tight text-[#1E3A5F]">
                Suggérer un questionnaire validé
              </DialogTitle>
              <DialogDescription className="text-[#5F758F] leading-relaxed">
                Proposez à notre équipe un questionnaire validé à ajouter à la bibliothèque partagée. Nous vérifions chaque suggestion avant intégration.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={submitSuggest} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="sug-title" className="text-[13px] font-semibold text-[#1E3A5F]">
                  Nom du questionnaire <span className="text-[#D45A3F]">*</span>
                </Label>
                <Input
                  id="sug-title"
                  value={suggest.title}
                  onChange={(e) => setSuggest((s) => ({ ...s, title: e.target.value }))}
                  placeholder="Ex. HADS – Hospital Anxiety and Depression Scale"
                  maxLength={200}
                  required
                  data-testid="suggest-title"
                  className="bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5]"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sug-reference" className="text-[13px] font-semibold text-[#1E3A5F]">
                  Référence scientifique <span className="text-[#D45A3F]">*</span>
                </Label>
                <Input
                  id="sug-reference"
                  value={suggest.reference}
                  onChange={(e) => setSuggest((s) => ({ ...s, reference: e.target.value }))}
                  placeholder="DOI, PubMed ID, éditeur ou lien vers l'article original"
                  maxLength={500}
                  required
                  data-testid="suggest-reference"
                  className="bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5]"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="sug-description" className="text-[13px] font-semibold text-[#1E3A5F]">
                  Description / justification clinique <span className="text-[#D45A3F]">*</span>
                </Label>
                <Textarea
                  id="sug-description"
                  value={suggest.description}
                  onChange={(e) => setSuggest((s) => ({ ...s, description: e.target.value }))}
                  placeholder="Population cible, indication, score, pourquoi il serait utile dans la bibliothèque…"
                  maxLength={2000}
                  required
                  rows={4}
                  data-testid="suggest-description"
                  className="bg-white border-[#E1E9F0] rounded-[10px] focus-visible:ring-[#3A8FA5] resize-none"
                />
                <p className="text-[11px] text-[#5F758F] text-right">{suggest.description.length} / 2000</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[13px] font-semibold text-[#1E3A5F]">
                  Pièce jointe PDF <span className="text-[#5F758F] font-normal">(optionnel, max 5 Mo)</span>
                </Label>
                {suggestPdf ? (
                  <div className="flex items-center gap-3 px-3 py-2 rounded-[10px] border border-[#E1E9F0] bg-[#F4F7FB]" data-testid="suggest-pdf-selected">
                    <Paperclip size={16} weight="duotone" className="text-[#3A8FA5] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-semibold text-[#1E3A5F] truncate">{suggestPdf.name}</p>
                      <p className="text-[11px] text-[#5F758F]">{Math.round(suggestPdf.size / 1024)} Ko</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setSuggestPdf(null)}
                      className="w-7 h-7 rounded-[8px] text-[#5F758F] hover:text-[#8B2A1E] hover:bg-[#FFE0DC] flex items-center justify-center shrink-0"
                      aria-label="Retirer le fichier"
                      data-testid="suggest-pdf-remove"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <label className="flex items-center gap-2 px-3 py-2 rounded-[10px] border border-dashed border-[#C7DEE6] bg-white text-[#5F758F] hover:border-[#3A8FA5] hover:text-[#1E3A5F] cursor-pointer transition-colors text-[13px]">
                    <Paperclip size={14} weight="duotone" className="text-[#3A8FA5]" />
                    Choisir un PDF
                    <input
                      type="file"
                      accept="application/pdf,.pdf"
                      className="hidden"
                      onChange={(e) => setSuggestPdf(e.target.files?.[0] || null)}
                      data-testid="suggest-pdf-input"
                    />
                  </label>
                )}
              </div>
              <DialogFooter className="pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setSuggestOpen(false); resetSuggest(); }}
                  disabled={suggestSending}
                  className="rounded-[10px] border-[#E1E9F0] text-[#5F758F]"
                  data-testid="suggest-cancel"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  disabled={suggestSending}
                  className="rounded-[10px] bg-[#3A8FA5] hover:bg-[#164E5F] text-white font-semibold"
                  data-testid="suggest-submit"
                >
                  {suggestSending ? "Envoi…" : "Envoyer la suggestion"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* AI-powered template generator */}
        <AITemplateDialog open={aiOpen} onOpenChange={setAiOpen} />
      </AppShell>
    </Protected>
  );
}
